import { type AllergyIntoleranceDto, recordAllergy, updateAllergy } from "@repo/data-store";
import { asPatientId } from "@repo/domain";
import { Button } from "@repo/ui/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@repo/ui/components/ui/dialog";
import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { focusFirstInvalid } from "@/shared/forms/focus-first-invalid";
import { useSubmitOnce } from "@/shared/forms/submit-once";

const TYPE_OPTIONS = [
  { value: "allergy", label: "Allergy" },
  { value: "intolerance", label: "Intolerance" },
] as const;
const CRITICALITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "high", label: "High" },
  { value: "unable-to-assess", label: "Unable to assess" },
] as const;
const CATEGORY_OPTIONS = [
  { value: "medication", label: "Medication" },
  { value: "food", label: "Food" },
  { value: "environment", label: "Environment" },
  { value: "biologic", label: "Biologic" },
] as const;
const VERIFICATION_OPTIONS = [
  { value: "unconfirmed", label: "Unconfirmed" },
  { value: "confirmed", label: "Confirmed" },
] as const;

const allergyFormSchema = z.object({
  substance: z.string().trim().min(1, "Enter a substance"),
  type: z.enum(["allergy", "intolerance"]),
  criticality: z.enum(["low", "high", "unable-to-assess"]),
  category: z.enum(["medication", "food", "environment", "biologic"]),
  verificationStatus: z.enum(["unconfirmed", "confirmed"]),
  manifestations: z.string(),
});

function parseReaction(manifestations: string) {
  const parts = manifestations
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? [{ manifestation: parts.map((text) => ({ text })) }] : undefined;
}

export interface AllergyDialogProps {
  patientId: string;
  editing: AllergyIntoleranceDto | null;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
}

export function AllergyDialog({ patientId, editing, open, onClose, onExited }: AllergyDialogProps) {
  const { once } = useSubmitOnce();
  const form = useAppForm({
    defaultValues: {
      substance: editing?.code.text ?? editing?.code.display ?? "",
      type: editing?.type ?? ("allergy" as const),
      criticality: editing?.criticality ?? ("low" as const),
      category: editing?.category?.[0] ?? ("medication" as const),
      verificationStatus:
        editing?.verificationStatus === "confirmed" ? ("confirmed" as const) : ("unconfirmed" as const),
      manifestations: editing?.reactions?.join(", ") ?? "",
    },
    validators: { onDynamic: allergyFormSchema },
    onSubmit: ({ value }) =>
      once((entityId) => {
        const reaction = parseReaction(value.manifestations);
        if (editing) {
          updateAllergy({
            allergyIntoleranceId: editing.allergyIntoleranceId,
            type: value.type,
            criticality: value.criticality,
            category: [value.category],
            verificationStatus: value.verificationStatus,
            reaction,
          });
        } else if (patientId) {
          recordAllergy({
            id: entityId,
            patient: { resourceType: "Patient", id: asPatientId(patientId) },
            code: { text: value.substance.trim() },
            clinicalStatus: "active",
            type: value.type,
            criticality: value.criticality,
            category: [value.category],
            verificationStatus: value.verificationStatus,
            reaction,
          });
        }
        onClose();
      }),
    onSubmitInvalid: focusFirstInvalid,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !next && onClose()}
      onOpenChangeComplete={(next) => !next && onExited()}
    >
      <DialogContent className="sm:max-w-md">
        <DialogTitle>{editing ? "Edit Allergy" : "Record Allergy"}</DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="mt-2 space-y-4"
        >
          <form.AppField name="substance">
            {(field) => <field.InputField label="Substance" placeholder="e.g. Penicillin" disabled={!!editing} />}
          </form.AppField>
          <form.AppField name="type">
            {(field) => <field.OptionGroupField size="sm" label="Type" options={[...TYPE_OPTIONS]} />}
          </form.AppField>
          <form.AppField name="criticality">
            {(field) => <field.OptionGroupField size="sm" label="Criticality" options={[...CRITICALITY_OPTIONS]} />}
          </form.AppField>
          <form.AppField name="category">
            {(field) => <field.OptionGroupField size="sm" label="Category" options={[...CATEGORY_OPTIONS]} />}
          </form.AppField>
          <form.AppField name="verificationStatus">
            {(field) => <field.OptionGroupField size="sm" label="Verification" options={[...VERIFICATION_OPTIONS]} />}
          </form.AppField>
          <form.AppField name="manifestations">
            {(field) => <field.InputField label="Reactions" placeholder="Comma-separated, e.g. Hives, Anaphylaxis" />}
          </form.AppField>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <form.AppForm>
              <form.SubmitButton disabled={!editing && !patientId}>
                {editing ? "Save" : "Record Allergy"}
              </form.SubmitButton>
            </form.AppForm>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
